import { useQuery } from '@tanstack/react-query'
import { apiService } from '../services/api'
import { LocalStorageService } from '../services/localStorage'

export function useFollowedShows() {
  return useQuery({
    queryKey: ['follows'],
    queryFn: () => apiService.getFollowedShows(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

export function useShow(showId: number | undefined) {
  return useQuery({
    queryKey: ['show', showId],
    queryFn: () => apiService.getShow(showId!),
    enabled: !!showId,
    staleTime: 30 * 60 * 1000, // 30 minutes
  })
}

export function useShowEpisodes(showId: number | undefined) {
  return useQuery({
    queryKey: ['episodes', showId],
    queryFn: () => apiService.getShowEpisodes(showId!),
    enabled: !!showId,
    staleTime: 30 * 60 * 1000,
  })
}

export function useMarkedEpisodes(showId?: number) {
  return useQuery({
    queryKey: ['marked', showId],
    queryFn: () => apiService.getMarkedEpisodes(showId),
    staleTime: 5 * 60 * 1000,
  })
}

// Hook for shows with local watched episodes
export function useWatchedShows() {
  const { data: follows, isLoading } = useFollowedShows()
  const watchedShowIds = LocalStorageService.getShowsWithLocalWatches()

  // Filter follows to only include those with local watches and extract embedded show data
  const showsWithLastWatched =
    follows
      ?.filter(follow => watchedShowIds.includes(follow.show_id))
      .map(follow => {
        const show = follow._embedded?.show
        if (!show) return null
        const lastWatched = LocalStorageService.getLastWatchedForShow(show.id)
        return { show, lastWatched }
      })
      .filter(Boolean) || []

  // Sort by last watched (desc) and take top 20
  showsWithLastWatched.sort((a, b) => {
    if (!a || !b) return 0
    return (b.lastWatched || 0) - (a.lastWatched || 0)
  })
  const recentlyWatched = showsWithLastWatched.slice(0, 20)

  return {
    data: recentlyWatched,
    isLoading,
    totalWatchedCount: showsWithLastWatched.length,
  }
}
